import axios from 'axios';
import {
  FLEX_PORTAL,
  REQUEST_PREFERRED_EMPLOYEES,
  ROSTER_REQUESTS,
  TASKS_OUTBOUND
} from '../endpoints';
import {
  AddFlexMatchRequestPayload,
  AskEmployeeMessageResponse,
  FlexMatchQualificationMutationPayload,
  FlexMatchTaskResponse,
  MatchCandidateResponse
} from './types';
import { RejectFlexMatchRequestPayload } from 'components/flex-portal/FlexMatch/hooks/useRejectFlexMatchRequest';
import { AcceptFlexMatchRequestPayload } from 'components/flex-portal/FlexMatch/hooks/useAcceptFlexMatchRequest';
import { FlexMatchAskEmployeeMessagePayload } from 'components/flex-portal/FlexMatch/hooks/useGetFlexMatchAskEmployeeMessage';
import { ConnectEmployeeToFlexMatchRequestPayload } from 'components/flex-portal/FlexMatch/hooks/useConnectEmployeeToFlexMatchRequest';
import { SendTaskMessageOutboundPayload } from 'components/flex-portal/FlexMatch/hooks/useSendTaskMessageOutbound';
import { UpdateFlexMatchRequestDutyCodeAndFunction } from 'components/flex-portal/FlexMatch/hooks/useUpdateFlexMatchRequestDutyCodeAndFunction';
import { Qualification } from 'components/types';
import { AcceptAllFlexMatchRequestPayload } from 'components/flex-portal/FlexMatch/hooks/useAcceptAllFlexMatchRequest';

export const getFlexMatchTasks = ({ filters, rangeStart, rangeEnd }) =>
  axios.get<FlexMatchTaskResponse[]>(`${FLEX_PORTAL}/tasks`, {
    params: {
      filters: JSON.stringify(filters)
    },
    headers: {
      Range: `items=${rangeStart}-${rangeEnd}`
    }
  });

export const addFlexMatchRequest = (payload: AddFlexMatchRequestPayload) =>
  axios.post<FlexMatchTaskResponse[]>(`${FLEX_PORTAL}/requests`, payload);

export const updateFlexMatchRequestDutyCodeAndFunction = (
  payload: UpdateFlexMatchRequestDutyCodeAndFunction
) =>
  axios.put<FlexMatchTaskResponse[]>(
    `${FLEX_PORTAL}/requests/${payload.requestId}`,
    payload.body
  );

export const getFlexMatchCandidates = ({ requestId }) =>
  axios.get<MatchCandidateResponse[]>(`${FLEX_PORTAL}/match/candidates`, {
    params: {
      requestId: JSON.stringify(requestId)
    }
  });

export const getFlexMatchEmployees = ({ requestId, filters }) =>
  axios.get<MatchCandidateResponse[]>(`${FLEX_PORTAL}/employees`, {
    params: {
      requestId: JSON.stringify(requestId),
      filters: JSON.stringify(filters)
    }
  });

export const addFlexMatchPreferredEmployee = ({ requestId, body }) =>
  axios.post(REQUEST_PREFERRED_EMPLOYEES, body, {
    pathParams: {
      requestId: JSON.stringify(requestId)
    }
  });

export const removeFlexMatchPreferredEmployee = ({ requestId, body }) =>
  axios.delete(REQUEST_PREFERRED_EMPLOYEES, {
    pathParams: {
      requestId: JSON.stringify(requestId)
    },
    params: {
      employeeId: body.employeeId,
      bpmTaskId: body.bpmTaskId
    }
  });

export const acceptFlexMatchRequest = ({
  taskId,
  body
}: AcceptFlexMatchRequestPayload) =>
  axios.put(`${FLEX_PORTAL}/tasks/${taskId}`, body);

export const acceptAllFlexMatchRequest = ({
  body
}: AcceptAllFlexMatchRequestPayload) =>
  axios.put(`${FLEX_PORTAL}/tasks/accept-all`, body);

export const rejectFlexMatchRequest = ({
  body
}: RejectFlexMatchRequestPayload) =>
  axios.delete(`${FLEX_PORTAL}/processes`, {
    data: body,
    pathParams: {
      requestId: JSON.stringify(body.requestId)
    },
    params: {
      processInstanceId: body.processInstanceId
    }
  });

export const getFlexMatchAskEmployeeMessage = ({
  workAgreementId,
  processInstanceId,
  employeeId,
  messageType
}: FlexMatchAskEmployeeMessagePayload) =>
  axios.get<AskEmployeeMessageResponse>(
    `${FLEX_PORTAL}/messages/FlexPortalMatchAskEmployee`,
    {
      params: {
        workAgreementId: JSON.stringify(workAgreementId),
        processInstanceId: JSON.stringify(processInstanceId),
        employeeId: JSON.stringify(employeeId),
        messageType: messageType ? messageType : undefined
      }
    }
  );

export const connectEmployeeToFlexMatchRequest = ({
  taskId,
  body
}: ConnectEmployeeToFlexMatchRequestPayload) =>
  axios.put(`${FLEX_PORTAL}/tasks/${taskId}`, body);

export const sendTaskMessageOutbound = ({
  askedTaskId,
  body
}: SendTaskMessageOutboundPayload) =>
  axios.post(TASKS_OUTBOUND, body, {
    pathParams: {
      askedTaskId
    }
  });

export const addOrRemoveFlexMatchQualification = (
  payload: FlexMatchQualificationMutationPayload
) => {
  const mutationType =
    payload.type === 'add' ? 'addQualification' : 'removeQualification';

  return axios.post<Qualification>(
    `${ROSTER_REQUESTS}/${mutationType}`,
    payload.body
  );
};
